import { useCallback, useRef, useState, useEffect } from "react";
import type { AudioError } from "@/types/audio";
import { AudioInputSource } from "@/types/audio";
import { Howl } from "howler";

export function useAudioRecorder(audioSource: AudioInputSource) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<AudioError | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const howlRef = useRef<Howl | null>(null);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      console.info("🎙️ AudioRecorder: Stopping recording");
      mediaRecorderRef.current.stop();
      console.debug("🎙️ AudioRecorder: Audio stream stopped");
    }
    audioSource.stopStream();
    setIsRecording(false);
  }, [audioSource]);

  const playRecordedAudio = useCallback(async () => {
    if (chunksRef.current.length === 0) {
      console.debug("🎙️ AudioRecorder: No audio chunks to play");
      return;
    }

    console.info("🎙️ AudioRecorder: Creating audio blob for playback");
    const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
    console.info(`🎙️ AudioRecorder: Using MIME type for blob: ${mimeType}`);
    
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const audioUrl = URL.createObjectURL(blob);
    
    // Stop any currently playing sound
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.unload();
    }

    // Extract format from MIME type
    const format = mimeType.split('/')[1]?.split(';')[0] || 'webm';
    console.info(`🎙️ AudioRecorder: Using format for Howl: ${format}`);

    // Create a new Howl instance with settings matching the actual format
    howlRef.current = new Howl({
      src: [audioUrl],
      format: [format],
      html5: false,
      preload: true,
      autoplay: false,
      onplay: () => {
        console.info("🎙️ AudioRecorder: Howl audio playback started");
        setIsPlaying(true);
      },
      onend: () => {
        console.info("🎙️ AudioRecorder: Howl audio playback completed");
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      },
      onloaderror: (id, error) => {
        console.error("🎙️ AudioRecorder: Howl loading error:", error);
        console.error("🎙️ AudioRecorder: Howl source:", audioUrl);
        console.error("🎙️ AudioRecorder: Blob size:", blob.size, "bytes");
        console.error("🎙️ AudioRecorder: MIME type:", mimeType);
        console.error("🎙️ AudioRecorder: Format:", format);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        setError({
          code: "PLAYBACK_ERROR",
          message: "Failed to load audio for playback",
          originalError: error
        });
      },
      onplayerror: (id, error) => {
        console.error("🎙️ AudioRecorder: Howl playback error:", error);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        setError({
          code: "PLAYBACK_ERROR",
          message: "Failed to play audio",
          originalError: error
        });
      }
    });

    // Start playback
    console.info("🎙️ AudioRecorder: Starting Howl audio playback");
    howlRef.current.play();
  }, []);

  const startRecording = useCallback(async () => {
    console.info("🎙️ AudioRecorder: Starting recording process");
    setError(null);
    chunksRef.current = [];

    try {
      console.debug(
        "🎙️ AudioRecorder: Requesting audio stream from source:",
        audioSource.constructor.name,
      );
      const stream = await audioSource.getStream();
      console.info("🎙️ AudioRecorder: Audio stream obtained successfully");

      // Try to use audio/webm for compatibility with Howler
      let options = {};
      try {
        const mimeTypes = ['audio/webm', 'audio/mp4', ''];
        for (const type of mimeTypes) {
          if (!type || MediaRecorder.isTypeSupported(type)) {
            options = { mimeType: type };
            console.info(`🎙️ AudioRecorder: Using MIME type: ${type || 'browser default'}`);
            break;
          }
        }
      } catch (e) {
        console.warn("🎙️ AudioRecorder: Error checking MIME type support:", e);
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      console.debug(
        "🎙️ AudioRecorder: MediaRecorder initialized with MIME type:",
        mediaRecorderRef.current.mimeType,
      );

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          console.debug(
            `🎙️ AudioRecorder: Received audio chunk: ${e.data.size} bytes`,
          );
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstart = () => {
        console.info("🎙️ AudioRecorder: MediaRecorder started successfully");
        setIsRecording(true);
      };

      mediaRecorderRef.current.onstop = () => {
        console.info(
          "🎙️ AudioRecorder: Recording stopped, preparing for playback",
        );
        playRecordedAudio();
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error("🎙️ AudioRecorder: MediaRecorder error:", event);
      };

      console.debug("🎙️ AudioRecorder: Starting MediaRecorder");
      mediaRecorderRef.current.start();
      return true;
    } catch (err) {
      console.error("🎙️ AudioRecorder: Error during recording setup:", err);
      setError({
        code: "RECORDING_ERROR",
        message:
          "Failed to access microphone. Please check your permissions and try again.",
        originalError: err,
      });
      return false;
    }
  }, [audioSource, playRecordedAudio]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Cleanup on unmount or when audio source changes
  useEffect(() => {
    return () => {
      console.info("🎙️ AudioRecorder: Cleaning up resources");
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      
      // Unload and stop any Howl instances
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.unload();
        howlRef.current = null;
      }
      
      audioSource.stopStream();
      setIsRecording(false);
      setIsPlaying(false);
      setError(null);
    };
  }, [audioSource]);

  return {
    isRecording,
    isPlaying,
    error,
    toggleRecording,
  };
}
